import React, { useEffect } from 'react';

import { useTranslation } from '@modernloop/i18n';
import { addMinutes, assertIsoTimestamp, getLocalTimezone, isBefore } from '@modernloop/shared/datetime';
import { Button, Tooltip } from '@mui/material';

import { OrgPrefName, useOrgPrefTimestamp } from 'src/hooks/api/org';

interface Props {
  handleConnect: () => void;
  disabled: boolean;
  dataTestId?: string;
}

export const ConnectAtsButton: React.FC<Props> = ({ handleConnect, disabled, dataTestId }) => {
  const { t } = useTranslation();
  const [atsDisconnectedTimestamp, { loading }] = useOrgPrefTimestamp(OrgPrefName.TS_ATS_DISCONNECTED_AT);
  const [inAtsConnectionDelay, setInAtsConnectionDelay] = React.useState<boolean>(false);
  const currentTimezone = getLocalTimezone();

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect synchronizes local state from external inputs; prefer derived state when possible.
  useEffect(() => {
    if (
      atsDisconnectedTimestamp &&
      isBefore(
        assertIsoTimestamp(new Date().toISOString()),
        addMinutes(assertIsoTimestamp(atsDisconnectedTimestamp), currentTimezone, 10)
      )
    ) {
      setInAtsConnectionDelay(true);
    }
  }, [atsDisconnectedTimestamp, currentTimezone]);

  if (inAtsConnectionDelay) {
    return (
      <Tooltip title={t('hiring-dashboard-settings-integrations:settings.integrations.connectAtsDelayTooltip')}>
        <div>
          <Button data-testid={`${dataTestId}-connect-button`} disabled>
            {t('hiring-dashboard-settings-integrations:settings.integrations.connect')}
          </Button>
        </div>
      </Tooltip>
    );
  }

  return (
    <Button
      data-testid={`${dataTestId}-connect-button`}
      disabled={loading || inAtsConnectionDelay || disabled}
      onClick={handleConnect}
    >
      {t('hiring-dashboard-settings-integrations:settings.integrations.connect')}
    </Button>
  );
};
